import { useCallback, useEffect, useState } from 'react'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { DateTimePicker } from '@progress/kendo-react-dateinputs'
import { Input, TextArea } from '@progress/kendo-react-inputs'
import { FaBullhorn, FaExclamationTriangle, FaInfoCircle, FaPlus } from 'react-icons/fa'

import {
  AnnouncementAdmin,
  AnnouncementPriority,
  deleteAnnouncement,
  endAnnouncement,
  getAllAnnouncements,
  saveAnnouncement,
} from '../../data/announcementData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
// The management screens share the Uniform page's card, table, badge and form
// styles rather than each keeping a copy.
import '../Uniform/Uniform.css'
import '../../components/Announcements/AnnouncementBanner.css'

const PRIORITIES: AnnouncementPriority[] = ['High', 'Normal', 'Low']

const PRIORITY_PILL: Record<AnnouncementPriority, string> = {
  High: 'uni-pill--declined',
  Normal: 'uni-pill--requested',
  Low: 'uni-pill--ordered',
}

const STATE_PILL: Record<AnnouncementAdmin['state'], string> = {
  Showing: 'uni-pill--delivered',
  Scheduled: 'uni-pill--ordered',
  Ended: 'uni-pill--cancelled',
}

const formatWhen = (iso: string | null, fallback: string) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : fallback

/**
 * Announcements for administrators: site-wide banners shown at the top of every
 * portal page until each user dismisses them.
 */
const AnnouncementsAdmin = () => {
  const { showToast } = useToast()
  const [announcements, setAnnouncements] = useState<AnnouncementAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<AnnouncementAdmin | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setAnnouncements(await getAllAnnouncements())
    } catch (error) {
      showToast(getApiErrorDetails(error, "Announcements couldn't be loaded.").message, 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    load()
  }, [load])

  const end = async (a: AnnouncementAdmin) => {
    if (!window.confirm(`Stop showing "${a.title}" to everyone now?`)) return
    try {
      await endAnnouncement(a.announcementId)
      showToast('Announcement ended', 'success')
      load()
    } catch (error) {
      showToast(getApiErrorDetails(error, "The announcement couldn't be ended.").message, 'error')
    }
  }

  const remove = async (a: AnnouncementAdmin) => {
    if (!window.confirm(`Delete "${a.title}"? It stops showing and is removed from this list.`)) return
    try {
      await deleteAnnouncement(a.announcementId)
      showToast('Announcement deleted', 'success')
      setAnnouncements((prev) => prev.filter((x) => x.announcementId !== a.announcementId))
    } catch (error) {
      showToast(getApiErrorDetails(error, "The announcement couldn't be deleted.").message, 'error')
    }
  }

  return (
    <div className='uni-page'>
      <header className='uni-header'>
        <div className='uni-header__inner' style={{ paddingBottom: '1rem' }}>
          <div>
            <h1 className='uni-title'>Announcements</h1>
            <p className='uni-subtitle'>
              Banners at the top of every portal page. Everyone sees them until they dismiss them, and once dismissed
              they don't come back.
            </p>
          </div>
          <button type='button' className='uni-btn uni-btn--accent' onClick={() => setEditing('new')}>
            <FaPlus /> New announcement
          </button>
        </div>
      </header>

      <div className='uni-body'>
        <section className='uni-card'>
          {loading ? (
            <div className='uni-empty'>Loading announcements…</div>
          ) : announcements.length === 0 ? (
            <div className='uni-empty'>No announcements yet.</div>
          ) : (
            <table className='uni-table'>
              <thead>
                <tr>
                  <th>Announcement</th>
                  <th>Priority</th>
                  <th>Shows</th>
                  <th>Status</th>
                  <th className='uni-num'>Dismissed by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {announcements.map((a) => (
                  <tr key={a.announcementId}>
                    <td style={{ maxWidth: '420px' }}>
                      <div style={{ fontWeight: 600 }}>{a.title}</div>
                      {a.message && (
                        <div className='uni-muted' style={{ whiteSpace: 'pre-line' }}>
                          {a.message.length > 160 ? `${a.message.slice(0, 160)}…` : a.message}
                        </div>
                      )}
                      <div className='uni-muted'>
                        Created {formatWhen(a.createdDate, '')}
                        {a.createdByName ? ` by ${a.createdByName}` : ''}
                      </div>
                    </td>
                    <td>
                      <span className={`uni-pill ${PRIORITY_PILL[a.priority]}`}>{a.priority}</span>
                    </td>
                    <td className='uni-muted'>
                      {formatWhen(a.startsAt, 'From creation')}
                      <br />
                      to {formatWhen(a.endsAt, 'until dismissed')}
                    </td>
                    <td>
                      <span className={`uni-pill ${STATE_PILL[a.state]}`}>{a.state}</span>
                    </td>
                    <td className='uni-num'>
                      {a.dismissedCount} {a.dismissedCount === 1 ? 'person' : 'people'}
                    </td>
                    <td>
                      <div className='uni-actions'>
                        <button type='button' className='uni-btn uni-btn--secondary uni-btn--small' onClick={() => setEditing(a)}>
                          Edit
                        </button>
                        {a.state !== 'Ended' && (
                          <button type='button' className='uni-btn uni-btn--secondary uni-btn--small' onClick={() => end(a)}>
                            End now
                          </button>
                        )}
                        <button type='button' className='uni-btn uni-btn--danger uni-btn--small' onClick={() => remove(a)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {editing && (
        <AnnouncementDialog
          announcement={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
    </div>
  )
}

const AnnouncementDialog = ({
  announcement,
  onClose,
  onSaved,
}: {
  announcement: AnnouncementAdmin | null
  onClose: () => void
  onSaved: () => void
}) => {
  const { showToast } = useToast()
  const [title, setTitle] = useState(announcement?.title ?? '')
  const [message, setMessage] = useState(announcement?.message ?? '')
  const [priority, setPriority] = useState<AnnouncementPriority>(announcement?.priority ?? 'Normal')
  const [startsAt, setStartsAt] = useState<Date | null>(announcement?.startsAt ? new Date(announcement.startsAt) : null)
  const [endsAt, setEndsAt] = useState<Date | null>(announcement?.endsAt ? new Date(announcement.endsAt) : null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setError(null)
    if (!title.trim()) return setError('Give the announcement a title.')
    if (startsAt && endsAt && endsAt <= startsAt) return setError('The end must be after the start.')

    setSaving(true)
    try {
      await saveAnnouncement(announcement?.announcementId ?? null, {
        title: title.trim(),
        message: message.trim() || null,
        priority,
        startsAt,
        endsAt,
      })
      showToast(announcement ? 'Announcement saved' : 'Announcement created', 'success')
      onSaved()
    } catch (err) {
      setError(getApiErrorDetails(err, "The announcement couldn't be saved.").message)
    } finally {
      setSaving(false)
    }
  }

  const icon = priority === 'High' ? <FaExclamationTriangle /> : priority === 'Low' ? <FaInfoCircle /> : <FaBullhorn />

  return (
    <Dialog title={announcement ? 'Edit announcement' : 'New announcement'} onClose={onClose} width={620}>
      <div className='uni-form uni-panel'>
        <div className='uni-field'>
          <label className='uni-field__label'>
            Title <em>*</em>
          </label>
          <Input value={title} maxLength={200} placeholder='e.g. Portal maintenance on Saturday' onChange={(e) => setTitle(String(e.value ?? ''))} />
        </div>
        <div className='uni-field'>
          <label className='uni-field__label'>Message</label>
          <TextArea value={message} rows={4} maxLength={2000} onChange={(e) => setMessage(String(e.value ?? ''))} />
        </div>
        <div className='uni-row'>
          <div className='uni-field'>
            <label className='uni-field__label'>Priority</label>
            <DropDownList data={PRIORITIES} value={priority} onChange={(e) => setPriority(e.value)} />
          </div>
          <div className='uni-field'>
            <label className='uni-field__label'>Starts</label>
            <DateTimePicker value={startsAt} format='dd/MM/yyyy HH:mm' onChange={(e) => setStartsAt(e.value)} />
            <span className='uni-field__hint'>Empty to show straight away.</span>
          </div>
          <div className='uni-field'>
            <label className='uni-field__label'>Ends</label>
            <DateTimePicker value={endsAt} format='dd/MM/yyyy HH:mm' onChange={(e) => setEndsAt(e.value)} />
            <span className='uni-field__hint'>Empty to show until dismissed.</span>
          </div>
        </div>

        <div className='uni-field'>
          <label className='uni-field__label'>Preview</label>
          <div className={`ann-banner ann-banner--${priority.toLowerCase()}`}>
            <span className='ann-banner__icon'>{icon}</span>
            <div className='ann-banner__body'>
              <div className='ann-banner__title'>{title.trim() || 'Your title'}</div>
              {message.trim() && <div className='ann-banner__message'>{message.trim()}</div>}
            </div>
          </div>
        </div>

        {announcement && announcement.dismissedCount > 0 && (
          <div className='uni-warning'>
            {announcement.dismissedCount} {announcement.dismissedCount === 1 ? 'person has' : 'people have'} already
            dismissed this and won't see your changes. For something they need to see again, create a new announcement.
          </div>
        )}

        {error && <div className='uni-error'>{error}</div>}
      </div>
      <DialogActionsBar>
        <button type='button' className='uni-btn uni-btn--secondary' onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type='button' className='uni-btn uni-btn--primary' onClick={save} disabled={saving}>
          {saving ? 'Saving…' : announcement ? 'Save' : 'Create announcement'}
        </button>
      </DialogActionsBar>
    </Dialog>
  )
}

export default AnnouncementsAdmin
